/**
 * CampaignStarterV3 — 4-mode campaign creation entrypoint at /campaign/v3/start.
 *
 * Modes: brief | creatives | cohort | product. The "Stellair agent reading
 * along" panel updates live as the user types/picks. Submit creates a
 * campaign and routes to the existing /campaigns/:id/plan-review.
 *
 * The whole page is scoped under the .csv3 wrapper class so the new --agent
 * token and pixel-perfect layout don't leak into other pages.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getApiUrl } from '../../utils/apiConfig';
import { getFolderDisplayLabel } from '../../utils/folderHierarchy';
import { useAccount } from '../../contexts/AccountContext';
import './CampaignStarterV3.css';

const MODES = [
  { key: 'brief',     name: 'A brief',            best: 'agency teams',   eta: '~3 min', desc: "Paste or upload a campaign brief. I'll extract goals, KPIs, audiences, and timing." },
  { key: 'creatives', name: 'A set of creatives', best: 'brand teams',    eta: '~2 min', desc: "Drop in a folder of assets. I'll group them by format, suggest channels, and back-fill the plan." },
  { key: 'cohort',    name: 'An audience cohort', best: 'CRM activations',eta: '~2 min', desc: "Pick an existing segment or upload a list. I'll find lookalikes and route to the right inventory." },
  { key: 'product',   name: 'A product release',  best: 'DTC & retail',   eta: '~2 min', desc: "Pull from your product catalog. I'll build a launch plan keyed to the release date." },
];

const COMPOSE_TITLES = {
  brief:     ['Describe the brief',           "I'll read it and propose a plan. You can edit anything."],
  creatives: ['Add your creatives',           "I'll group them by format and back-fill the rest of the plan."],
  cohort:    ['Choose your audience',         "I'll find the channels and inventory that reach them best."],
  product:   ['Select a product release',     "I'll build a launch plan keyed to the release date."],
};

const BRIEF_EXAMPLES = {
  awareness:   "Q3 awareness push for Acme Co's repositioning. Total budget ~$250K, flighting Aug 1 – Sep 30. Primary audience: 25-44 urban, mid-to-high HHI. KPIs: unique reach 8M+, video completion >70%. Channels: open to DV360, Meta, TTD CTV. Avoid news adjacency. Brand-safe inventory only.",
  dtc:         "Launch Aurora wireless headphones to DTC audience. $180K budget over 6 weeks starting Jun 14. Target: audiophiles + commuters 22-40. Want strong video story (15s + 6s cuts) plus retargeting. KPIs: site traffic, add-to-cart, ROAS 3x+. Channels: Meta, Spotify audio, YouTube.",
  retargeting: "Retargeting cart abandoners for Q3. Budget $80K. Audience cohort already defined (cart_abandoners_30d). Need creative rotation to avoid fatigue. KPIs: conversion rate, CPA <$45. Run on Meta + DV360 web. 4 week flight starting next Monday.",
};

// ─── Inline icons (matches the prototype, lucide-style strokes) ───
const I = {
  spark: (size = 14) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5z"/></svg>
  ),
  sparkLg: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="#fff"><path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5z"/><path d="M19 15l.7 2.1L22 18l-2.3.9L19 21l-.7-2.1L16 18l2.3-.9z"/></svg>
  ),
  brief: (size = 20) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
  ),
  image: (size = 20) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
  ),
  users: (size = 20) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
  ),
  box: (size = 20) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
  ),
  check: (size = 10) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20,6 9,17 4,12"/></svg>
  ),
  upload: (size = 18) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
  ),
  play: (size = 20) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><polygon points="6,4 20,12 6,20"/></svg>
  ),
  info: (size = 13) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
  ),
  chevronR: (size = 14) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9,6 15,12 9,18"/></svg>
  ),
  chevronD: (size = 12) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6,9 12,15 18,9"/></svg>
  ),
  x: (size = 12) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
  ),
  cohortSegment: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
  ),
  cohortVip: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
  ),
  cohortGlobe: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
  ),
  cohortPulse: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
  ),
  productHeadphones: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1v-7h3zM3 19a2 2 0 0 0 2 2h1v-7H3z"/></svg>
  ),
  productStar: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
  ),
  productSmile: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
  ),
};

const COHORT_ICONS = { segment: I.cohortSegment, vip: I.cohortVip, globe: I.cohortGlobe, pulse: I.cohortPulse };
const PRODUCT_ICONS = { headphones: I.productHeadphones, star: I.productStar, smile: I.productSmile };

// ─── Folder picker ───
function FolderPicker({ folders, selected, onSelect, loading }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <div className="csv3-folderpicker" ref={wrapRef}>
      <button className="csv3-folderpicker-btn" onClick={() => setOpen((o) => !o)} type="button">
        {selected
          ? <span>{getFolderDisplayLabel(selected)}</span>
          : <span className="placeholder">Choose folder…</span>}
        {I.chevronD()}
      </button>
      {open && (
        <div className="csv3-folderpicker-menu">
          {loading && <div className="csv3-folderpicker-empty">Loading folders…</div>}
          {!loading && folders.length === 0 && <div className="csv3-folderpicker-empty">No folders yet — create one first</div>}
          {!loading && folders.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`csv3-folderpicker-item ${selected?.id === f.id ? 'on' : ''}`}
              onClick={() => { onSelect(f); setOpen(false); }}
            >
              <div>{getFolderDisplayLabel(f)}</div>
              <div className="meta">{f.campaign_count ?? 0} campaign{(f.campaign_count ?? 0) === 1 ? '' : 's'}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Mode picker ───
function ModePicker({ mode, onChange }) {
  const iconFor = (k) => k === 'brief' ? I.brief() : k === 'creatives' ? I.image() : k === 'cohort' ? I.users() : I.box();
  return (
    <div className="csv3-modes">
      {MODES.map((m) => (
        <button
          key={m.key}
          type="button"
          className={`csv3-mode ${m.key} ${mode === m.key ? 'selected' : ''}`}
          onClick={() => onChange(m.key)}
        >
          <div className="check">{I.check()}</div>
          <div className="ic">{iconFor(m.key)}</div>
          <div className="name">{m.name}</div>
          <div className="desc">{m.desc}</div>
          <div className="foot">Best for · {m.best}<span className="dot" />{m.eta}</div>
        </button>
      ))}
    </div>
  );
}

// ─── Agent readback panel ───
function AgentReadbackPanel({ rows, thinking }) {
  return (
    <div className="csv3-readback">
      <div className="csv3-rb-h">
        <div className="av">{I.spark()}</div>
        <div>
          <div className="l">Stellair agent · reading along</div>
          <div className="t">
            <span className={`pulse-d ${thinking ? 'thinking' : ''}`} />
            {thinking ? 'Thinking…' : "What I'm understanding so far"}
          </div>
        </div>
      </div>
      <div>
        {rows.length === 0 && (
          <div className="csv3-rb-row">
            <div className="k">Objective</div>
            <div className="v idle">Waiting for your input… I'll start interpreting once you've added some context.</div>
          </div>
        )}
        {rows.map((r, i) => (
          <div className="csv3-rb-row" key={i}>
            <div className="k">{r.k}</div>
            <div className="v" dangerouslySetInnerHTML={{ __html: r.v }} />
            {r.conf && <span className={`conf ${r.confLevel === 'med' ? 'med' : ''}`}>{r.conf}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Brief mode ───
function ComposeBrief({ name, setName, brief, setBrief, budget, setBudget, flight, setFlight }) {
  return (
    <>
      <div className="csv3-compose-h">
        <div className="ic">{I.brief(14)}</div>
        <div>
          <div className="t">Campaign brief</div>
          <div className="s">Type, paste, or upload — natural language is fine</div>
        </div>
        <div className="modeswitch">
          <button className="ms-btn on" type="button">Type</button>
          <button className="ms-btn" type="button" disabled title="Coming soon">Upload .pdf / .docx</button>
        </div>
      </div>
      <div className="csv3-compose-body">
        <div className="csv3-field">
          <div className="csv3-field-l">
            Campaign name
            <span className="ag-hint">{I.spark(11)} I'll suggest one from your brief</span>
          </div>
          <input className="csv3-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Q3 Brand Refresh — Acme Co" />
        </div>
        <div className="csv3-field">
          <div className="csv3-field-l">Brief <span className="opt">required</span></div>
          <textarea
            className="csv3-textarea csv3-brief-area"
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            placeholder="Paste or write your brief. Mention objective, audience, timing, budget, channels you've considered, KPIs, and any constraints. The more context, the better the plan I can draft."
          />
          <div className="csv3-examples">
            <span className="label">Or try:</span>
            {Object.entries(BRIEF_EXAMPLES).map(([k, txt]) => (
              <button key={k} type="button" className="csv3-ex" onClick={() => setBrief(txt)}>
                <span className="ic">{I.spark(11)}</span>
                {k === 'awareness' ? 'Q3 awareness push, $250K' : k === 'dtc' ? 'DTC product launch' : 'Retargeting cart abandoners'}
              </button>
            ))}
          </div>
        </div>
        <div className="csv3-field-row">
          <div className="csv3-field">
            <div className="csv3-field-l">Total budget <span className="opt">optional</span></div>
            <div className="csv3-input-wrap">
              <span className="prefix">$</span>
              <input className="csv3-input with-prefix" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="250,000" />
            </div>
          </div>
          <div className="csv3-field">
            <div className="csv3-field-l">Flight <span className="opt">optional</span></div>
            <input className="csv3-input" value={flight} onChange={(e) => setFlight(e.target.value)} placeholder="Aug 1 – Sep 30, 2026" />
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Creatives mode ───
function ComposeCreatives({ name, setName, assets, onUpload, onRemove }) {
  const [over, setOver] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    onUpload(Array.from(fileList));
  };

  return (
    <>
      <div className="csv3-compose-h">
        <div className="ic">{I.image(14)}</div>
        <div>
          <div className="t">Creative assets</div>
          <div className="s">Drop in images, videos, or HTML5 — I'll fingerprint specs and group them</div>
        </div>
      </div>
      <div className="csv3-compose-body">
        <div className="csv3-field">
          <div className="csv3-field-l">Campaign name <span className="opt">optional · I'll generate one</span></div>
          <input className="csv3-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Holiday 2026 — Gift Guide" />
        </div>
        <div className="csv3-field">
          <div className="csv3-field-l">Assets</div>
          <div
            className={`csv3-drop ${over ? 'over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setOver(true); }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); handleFiles(e.dataTransfer.files); }}
            onClick={() => inputRef.current?.click()}
          >
            <div className="ic">{I.upload()}</div>
            <div className="t">Drop a folder, or <b>browse files</b></div>
            <div className="s">PNG · JPG · MP4 · MOV · GIF · HTML5 zips · up to 500MB total</div>
            <input ref={inputRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => handleFiles(e.target.files)} />
          </div>
          {assets.length > 0 && (
            <div className="csv3-asset-grid">
              {assets.map((a, i) => (
                <div key={i} className={`csv3-asset ${a.loading ? 'loading' : ''}`}>
                  <div className={`thumb ${a.kind === 'video' ? 'video' : ''}`}>
                    {a.kind === 'video' ? I.play() : I.image(22)}
                  </div>
                  {(a.format || a.loading) && (
                    <div className="ag-tag">
                      {a.loading ? '…analyzing' : `★ ${a.format}`}
                    </div>
                  )}
                  <div className="nm" title={a.file}>{a.file}</div>
                  <div className="meta">
                    <span>{a.dimensions || a.mime || '—'}</span>
                    <span>{formatBytes(a.size)}</span>
                  </div>
                  <button className="x" type="button" onClick={() => onRemove(i)}>{I.x()}</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Cohort mode ───
function ComposeCohort({ name, setName, cohorts, selectedId, onSelect, loading }) {
  return (
    <>
      <div className="csv3-compose-h">
        <div className="ic">{I.users(14)}</div>
        <div>
          <div className="t">Audience cohort</div>
          <div className="s">Choose an existing segment or upload a CSV / hashed list</div>
        </div>
      </div>
      <div className="csv3-compose-body">
        <div className="csv3-field">
          <div className="csv3-field-l">Campaign name</div>
          <input className="csv3-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Loyalty Reactivation — Q3" />
        </div>
        <div className="csv3-field">
          <div className="csv3-field-l">
            Pick a cohort <span className="ag-hint">From your CDP & saved segments</span>
          </div>
          {loading && <div className="csv3-loading">Loading cohorts…</div>}
          {!loading && (
            <div className="csv3-cohort-list">
              {cohorts.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`csv3-ch ${selectedId === c.id ? 'on' : ''}`}
                  onClick={() => onSelect(c.id)}
                >
                  <div className="icx">{COHORT_ICONS[c.icon] || I.cohortSegment}</div>
                  <div>
                    <div className="nm">{c.name}</div>
                    <div className="sz">{c.size} · {c.detail}</div>
                  </div>
                  <div className="check">{I.check()}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="csv3-field">
          <div className="csv3-field-l">Or upload a list <span className="opt">optional</span></div>
          <div className="csv3-drop" style={{ padding: 18 }}>
            <div className="t" style={{ fontSize: 12.5 }}>Drop a <b>.csv</b> or hashed-email file</div>
            <div className="s">We'll match against DV360, Meta, and TTD audience clouds.</div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Product mode ───
function ComposeProduct({ name, setName, products, selectedId, onSelect, loading }) {
  return (
    <>
      <div className="csv3-compose-h">
        <div className="ic">{I.box(14)}</div>
        <div>
          <div className="t">Product release</div>
          <div className="s">Pulled from your product catalog · synced 14h ago</div>
        </div>
      </div>
      <div className="csv3-compose-body">
        <div className="csv3-field">
          <div className="csv3-field-l">Campaign name</div>
          <input className="csv3-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Aurora Headphones — Launch" />
        </div>
        <div className="csv3-field">
          <div className="csv3-field-l">
            Pick a release <span className="ag-hint">{products.length} upcoming</span>
          </div>
          {loading && <div className="csv3-loading">Loading releases…</div>}
          {!loading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {products.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`csv3-product ${selectedId === p.id ? 'on' : ''}`}
                  onClick={() => onSelect(p.id)}
                >
                  <div className="pic">{PRODUCT_ICONS[p.icon] || I.productStar}</div>
                  <div className="info">
                    <div className="nm">{p.name}</div>
                    <div className="meta">
                      <span>{p.channel}</span><span className="dot">·</span>
                      <span>{p.msrp}</span><span className="dot">·</span>
                      <span>{p.category}</span>
                    </div>
                  </div>
                  <div className="date">Launches {p.launch_date_label}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Helpers ───
function formatBytes(n) {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function inferAssetKind(file) {
  const t = (file.type || '').toLowerCase();
  const n = (file.name || '').toLowerCase();
  if (t.startsWith('video/') || /\.(mp4|mov|webm)$/.test(n)) return 'video';
  if (t.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/.test(n)) return 'image';
  if (t.includes('zip') || /\.zip$/.test(n)) return 'zip';
  return 'file';
}

// Debounce hook
function useDebounced(value, ms) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return d;
}

// ─── Main page ───
function CampaignStarterV3() {
  const navigate = useNavigate();
  const { selectedAccountId, user } = useAccount();
  const accountId = selectedAccountId;

  // Mode state
  const [mode, setMode] = useState('brief');

  // Folder
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [folder, setFolder] = useState(null);

  // Brief
  const [briefName, setBriefName] = useState('');
  const [brief, setBrief] = useState('');
  const [briefBudget, setBriefBudget] = useState('');
  const [briefFlight, setBriefFlight] = useState('');

  // Creatives
  const [crName, setCrName] = useState('');
  const [assets, setAssets] = useState([]); // [{ file, kind, size, mime, gcsPath, format, dimensions, duration_s, loading, suggested_channels }]

  // Cohort
  const [coName, setCoName] = useState('');
  const [cohorts, setCohorts] = useState([]);
  const [cohortsLoading, setCohortsLoading] = useState(false);
  const [selectedCohort, setSelectedCohort] = useState(null);

  // Product
  const [prName, setPrName] = useState('');
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Readback
  const [readback, setReadback] = useState([]);
  const [readbackThinking, setReadbackThinking] = useState(false);

  // Submit
  const [submitting, setSubmitting] = useState(false);

  // Fetch folders once
  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    (async () => {
      setFoldersLoading(true);
      try {
        // Name sort groups sub-folders under their parent; limit raised from
        // the API default (10) so the picker shows the full folder list, same
        // as CampaignLaunchWorkflow
        const res = await api.get(getApiUrl('/api/v2/folders'), {
          params: { accountId, limit: 100, sortBy: 'name', sortOrder: 'asc' }
        });
        if (cancelled) return;
        const list = res.data?.folders || [];
        setFolders(list);
        if (list.length > 0) setFolder(list[0]); // sensible default
      } catch (e) {
        if (!cancelled) console.error('Failed to load folders', e);
      } finally {
        if (!cancelled) setFoldersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [accountId]);

  // Fetch cohorts when entering cohort mode (cache first time)
  useEffect(() => {
    if (mode !== 'cohort' || cohorts.length > 0 || !accountId) return;
    let cancelled = false;
    (async () => {
      setCohortsLoading(true);
      try {
        const res = await api.get(getApiUrl('/api/v2/campaign-starter/cohorts'), { params: { accountId } });
        if (cancelled) return;
        const list = res.data?.cohorts || [];
        setCohorts(list);
        if (list.length > 0) setSelectedCohort(list[0].id);
      } catch (e) {
        if (!cancelled) console.error('Failed to load cohorts', e);
      } finally {
        if (!cancelled) setCohortsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, accountId, cohorts.length]);

  // Fetch products when entering product mode (cache first time)
  useEffect(() => {
    if (mode !== 'product' || products.length > 0 || !accountId) return;
    let cancelled = false;
    (async () => {
      setProductsLoading(true);
      try {
        const res = await api.get(getApiUrl('/api/v2/campaign-starter/releases'), { params: { accountId } });
        if (cancelled) return;
        const list = res.data?.releases || [];
        setProducts(list);
        if (list.length > 0) setSelectedProduct(list[0].id);
      } catch (e) {
        if (!cancelled) console.error('Failed to load releases', e);
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, accountId, products.length]);

  // ─── Brief readback (debounced LLM call) ───
  const debouncedBrief = useDebounced(brief, 500);
  useEffect(() => {
    if (mode !== 'brief') return;
    const txt = (debouncedBrief || '').trim();
    if (txt.length < 30) {
      setReadback([]);
      setReadbackThinking(false);
      return;
    }
    let cancelled = false;
    setReadbackThinking(true);
    (async () => {
      try {
        const res = await api.post(getApiUrl('/api/v2/campaign-starter/extract-brief'), {
          brief: txt,
          name: briefName,
        });
        if (cancelled) return;
        const e = res.data || {};
        const fmtChips = (arr) => (Array.isArray(arr) ? arr : [])
          .map((c) => `<span class="csv3-rb-tag">${escapeHtml(c)}</span>`).join('');
        const conf = (n) => {
          if (n == null) return null;
          const pct = Math.round(n * 100);
          return { conf: `${pct}%`, confLevel: pct < 80 ? 'med' : '' };
        };
        const rows = [
          { k: 'Campaign type',         v: `<b>${escapeHtml(e.objective?.value || '—')}</b>`, ...(conf(e.objective?.confidence) || {}) },
          { k: 'Budget',                v: escapeHtml(e.budget?.value || "Not specified — I'll leave open"), ...(conf(e.budget?.confidence) || {}) },
          { k: 'Primary audience',      v: escapeHtml(e.audience?.value || '—'), ...(conf(e.audience?.confidence) || {}) },
          { k: 'Recommended channels',  v: fmtChips(e.channels?.value), ...(conf(e.channels?.confidence) || {}) },
          { k: 'KPIs',                  v: escapeHtml(e.kpis?.value || '—'), ...(conf(e.kpis?.confidence) || {}) },
          { k: 'Next steps',            v: escapeHtml(e.next_steps || 'Draft media plan with channel splits, suggest creative specs needed, set pacing & guardrails.') },
        ];
        setReadback(rows);
      } catch (err) {
        if (!cancelled) {
          console.error('extract-brief failed', err);
          setReadback([{ k: 'Error', v: 'Could not reach the agent. Try again in a moment.', conf: '', confLevel: 'med' }]);
        }
      } finally {
        if (!cancelled) setReadbackThinking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, debouncedBrief, briefName]);

  // ─── Creatives readback ───
  useEffect(() => {
    if (mode !== 'creatives') return;
    if (assets.length === 0) {
      setReadback([]);
      return;
    }
    const ready = assets.filter((a) => !a.loading && a.format);
    if (ready.length === 0) {
      setReadback([{ k: 'Assets detected', v: `<b>${assets.length} files</b> · analyzing…`, conf: '…', confLevel: 'med' }]);
      return;
    }
    const formats = [...new Set(ready.map((a) => a.format))];
    const channels = [...new Set(ready.flatMap((a) => a.suggested_channels || []))];
    const videoCount = ready.filter((a) => a.kind === 'video').length;
    const stillCount = ready.filter((a) => a.kind === 'image').length;
    setReadback([
      { k: 'Assets detected',     v: `<b>${ready.length} files</b> · ${videoCount} video · ${stillCount} still`, conf: '99%' },
      { k: 'Format groups',       v: formats.map((f) => `<span class="csv3-rb-tag">${escapeHtml(f)}</span>`).join(''), conf: '96%' },
      { k: 'Suggested channels',  v: channels.map((c) => `<span class="csv3-rb-tag">${escapeHtml(c)}</span>`).join(''), conf: '88%' },
      { k: 'Next steps',          v: "I'll fingerprint each asset, propose channel mapping, and flag missing specs." },
    ]);
  }, [mode, assets]);

  // ─── Cohort readback ───
  useEffect(() => {
    if (mode !== 'cohort') return;
    const c = cohorts.find((x) => x.id === selectedCohort);
    if (!c) {
      setReadback([]);
      return;
    }
    setReadback([
      { k: 'Cohort',               v: `<b>${escapeHtml(c.name)}</b>`, conf: '100%' },
      { k: 'Size',                 v: escapeHtml(`${c.size} · ${c.detail}`) },
      { k: 'Match rate (est.)',    v: (c.match_rates || []).map((m) => `<span class="csv3-rb-tag">${escapeHtml(m)}</span>`).join(''), conf: '85%' },
      { k: 'Lookalike opportunity', v: escapeHtml(c.lookalike || ''), conf: '82%', confLevel: 'med' },
      { k: 'Recommended funnel',   v: escapeHtml(c.recommended_funnel || ''), conf: '90%' },
      { k: 'Next steps',           v: 'Sync cohort to platforms, propose frequency caps, draft creative rotation plan.' },
    ]);
  }, [mode, cohorts, selectedCohort]);

  // ─── Product readback ───
  useEffect(() => {
    if (mode !== 'product') return;
    const p = products.find((x) => x.id === selectedProduct);
    if (!p) {
      setReadback([]);
      return;
    }
    setReadback([
      { k: 'Product',         v: `<b>${escapeHtml(p.name)}</b>`, conf: '100%' },
      { k: 'Launch window',   v: escapeHtml(`Launches ${p.launch_date_label} · 6-week flight recommended`), conf: '92%' },
      { k: 'Funnel mix',      v: 'Pre-launch teaser (2wk) → launch burst (1wk) → consideration (3wk)', conf: '88%' },
      { k: 'Channels',        v: (p.suggested_channels || []).map((c) => `<span class="csv3-rb-tag">${escapeHtml(c)}</span>`).join(''), conf: '90%' },
      { k: 'Catalog feed',    v: `Auto-generated DPA from your product catalog · ${p.sku_count || 0} SKUs`, conf: '95%' },
      { k: 'Next steps',      v: 'Draft launch plan, suggest creative specs, set up DPA feed.' },
    ]);
  }, [mode, products, selectedProduct]);

  // ─── Creatives upload + analyze ───
  const onCreativesUpload = useCallback(async (files) => {
    // Push placeholder rows so UI feels responsive
    const placeholders = files.map((f) => ({
      file: f.name,
      kind: inferAssetKind(f),
      size: f.size,
      mime: f.type || '—',
      loading: true,
    }));
    setAssets((prev) => [...prev, ...placeholders]);

    // Upload + analyze in parallel per file
    await Promise.all(files.map(async (file, idx) => {
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('accountId', accountId);
        fd.append('userId', user?.userId || user?.id || '');
        const upRes = await api.post(getApiUrl('/api/upload'), fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const gcsPath = upRes.data?.data?.gcsPath;

        const an = await api.post(getApiUrl('/api/v2/orchestration/analyze-assets'), { gcsPaths: [gcsPath] });
        const meta = an.data?.assets?.[0] || {};

        setAssets((prev) => prev.map((a) => (a === placeholders[idx] ? {
          ...a,
          loading: false,
          gcsPath,
          format: meta.format,
          dimensions: meta.dimensions,
          duration_s: meta.duration_s,
          suggested_channels: meta.suggested_channels || [],
          mime: meta.mime || a.mime,
        } : a)));
      } catch (err) {
        console.error('Asset upload/analyze failed', err);
        setAssets((prev) => prev.map((a) => (a === placeholders[idx] ? { ...a, loading: false, format: 'unknown' } : a)));
      }
    }));
  }, [accountId, user]);

  const onAssetRemove = (i) => setAssets((prev) => prev.filter((_, idx) => idx !== i));

  // ─── Submit ───
  const canSubmit = useMemo(() => {
    if (!folder || !accountId) return false;
    if (submitting) return false;
    switch (mode) {
      case 'brief':     return brief.trim().length >= 30;
      case 'creatives': return assets.length > 0 && assets.every((a) => !a.loading);
      case 'cohort':    return Boolean(selectedCohort);
      case 'product':   return Boolean(selectedProduct);
      default: return false;
    }
  }, [mode, folder, accountId, brief, assets, selectedCohort, selectedProduct, submitting]);

  const onSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const baseName =
        mode === 'brief'     ? (briefName || 'Untitled brief')
      : mode === 'creatives' ? (crName    || 'Untitled creative campaign')
      : mode === 'cohort'    ? (coName    || 'Untitled cohort campaign')
      :                        (prName    || products.find((p) => p.id === selectedProduct)?.name || 'Untitled product launch');

      const modePayload =
        mode === 'brief'     ? { brief: brief.trim(), budget: briefBudget, flight: briefFlight }
      : mode === 'creatives' ? { assets: assets.map((a) => ({ gcsPath: a.gcsPath, format: a.format, file: a.file })) }
      : mode === 'cohort'    ? { cohortId: selectedCohort }
      :                        { productId: selectedProduct };

      const payload = {
        name: baseName,
        folderId: folder.id,
        accountId,
        starterMode: mode,
        starterPayload: modePayload,
        draftedPlan: readback.map((r) => ({ key: r.k, value: r.v, confidence: r.conf })),
      };

      const res = await api.post(getApiUrl('/api/v2/campaign-starter/draft'), payload);
      const target = res.data?.redirectTo || '/campaigns';
      navigate(target, {
        state: {
          fromStarter: true,
          starterMode: mode,
          draftId: res.data?.id,
          message: `Draft submitted — the agent will start working on your ${mode}.`,
        },
      });
    } catch (err) {
      console.error('Draft my campaign failed', err);
      alert('Could not draft your campaign. ' + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="csv3">
      {/* Crumb bar with folder picker */}
      <div className="csv3-crumbbar">
        <div className="crumb">
          <span>Campaigns</span>
          <span className="sep">/</span>
          <FolderPicker folders={folders} selected={folder} onSelect={setFolder} loading={foldersLoading} />
          <span className="sep">/</span>
          <b>New campaign</b>
        </div>
        <div className="spacer" />
      </div>

      <div className="csv3-page">
        {/* Hero */}
        <div className="csv3-hero">
          <div className="av">{I.sparkLg}</div>
          <div>
            <span className="badge-new">★ Stellair Agent · Campaign starter</span>
            <h1>Tell me what you want to launch.</h1>
            <p>Drop in a brief, a folder of creatives, an audience cohort, or a product release. I'll draft the media plan, target the right channels, and surface anything I'm unsure about — you stay in control.</p>
          </div>
        </div>

        {/* Mode picker */}
        <div className="csv3-sech">
          <span className="num">01</span>
          <span className="t">Start from</span>
          <span className="s">Pick the input you have on hand. You can add the others as we go.</span>
        </div>
        <ModePicker mode={mode} onChange={setMode} />

        {/* Compose */}
        <div className="csv3-sech">
          <span className="num">02</span>
          <span className="t">{COMPOSE_TITLES[mode][0]}</span>
          <span className="s">{COMPOSE_TITLES[mode][1]}</span>
        </div>

        <div className="csv3-compose">
          {mode === 'brief' && (
            <ComposeBrief
              name={briefName} setName={setBriefName}
              brief={brief} setBrief={setBrief}
              budget={briefBudget} setBudget={setBriefBudget}
              flight={briefFlight} setFlight={setBriefFlight}
            />
          )}
          {mode === 'creatives' && (
            <ComposeCreatives
              name={crName} setName={setCrName}
              assets={assets}
              onUpload={onCreativesUpload}
              onRemove={onAssetRemove}
            />
          )}
          {mode === 'cohort' && (
            <ComposeCohort
              name={coName} setName={setCoName}
              cohorts={cohorts}
              selectedId={selectedCohort}
              onSelect={setSelectedCohort}
              loading={cohortsLoading}
            />
          )}
          {mode === 'product' && (
            <ComposeProduct
              name={prName} setName={setPrName}
              products={products}
              selectedId={selectedProduct}
              onSelect={setSelectedProduct}
              loading={productsLoading}
            />
          )}

          <AgentReadbackPanel rows={readback} thinking={readbackThinking} />

          <div className="csv3-compose-body" style={{ paddingTop: 0 }}>
            <div className="csv3-actions">
              <div className="info">
                {I.info()}
                You'll review the full plan before anything is trafficked.
              </div>
              <button className="csv3-btn csv3-btn-secondary" type="button" disabled={submitting}>Save draft</button>
              <button className="csv3-btn csv3-btn-agent csv3-btn-lg" type="button" onClick={onSubmit} disabled={!canSubmit}>
                {I.spark(14)}
                {submitting ? 'Drafting your campaign…' : 'Draft my campaign'}
                {!submitting && I.chevronR()}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function escapeHtml(s) {
  return String(s ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

export default CampaignStarterV3;
